import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './App.css';

const API_BASE = 'https://api.tomassage.id';

// Format currency IDR
const formatRupiah = (num) => {
  if (num === null || num === undefined) return 'Rp 0';
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

// ========== TOAST NOTIFICATION ==========
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 3500);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`toast toast-${type}`}>
      <span className="toast-icon">
        {type === 'success' ? '✓' : type === 'error' ? '✕' : 'ℹ'}
      </span>
      <span className="toast-msg">{message}</span>
      <button className="toast-close" onClick={onClose}>×</button>
    </div>
  );
}

// ========== MODAL COMPONENT ==========
function Modal({ isOpen, onClose, title, children }) {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

// ========== CONFIRM DIALOG ==========
function ConfirmDialog({ isOpen, title, message, onConfirm, onCancel }) {
  if (!isOpen) return null;
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="confirm-icon">⚠️</div>
        <h3>{title}</h3>
        <p>{message}</p>
        <div className="confirm-actions">
          <button className="btn btn-ghost" onClick={onCancel}>Batal</button>
          <button className="btn btn-danger" onClick={onConfirm}>Ya, Lanjutkan</button>
        </div>
      </div>
    </div>
  );
}

// ========== SALDO CARD ==========
function SaldoCard({ mitra, saldo, onAddBalance, onSubtractBalance, onViewTransactions, onInitialize }) {
  const hasBalance = saldo !== null;

  return (
    <div className="saldo-card">
      <div className="saldo-card-header">
        <div className="mitra-avatar">
          {mitra.foto_profil ? (
            <img src={mitra.foto_profil} alt={mitra.nama_lengkap} />
          ) : (
            <span>{mitra.nama_lengkap?.charAt(0)?.toUpperCase()}</span>
          )}
          <span className={`status-dot ${mitra.status === 'active' ? 'active' : 'inactive'}`} />
        </div>
        <div className="mitra-info">
          <h4>{mitra.nama_lengkap}</h4>
          <span className="mitra-phone">{mitra.nomor_wa}</span>
          <span className={`mitra-badge ${mitra.status}`}>
            {mitra.status === 'active' ? 'Aktif' : 'Nonaktif'}
          </span>
        </div>
        <div className="mitra-id">#{mitra.id}</div>
      </div>

      {hasBalance ? (
        <>
          <div className="saldo-amount-section">
            <div className="saldo-label">Saldo Saat Ini</div>
            <div className={`saldo-amount ${(saldo.current_balance || 0) < 10000 ? 'low' : ''}`}>
              {formatRupiah(saldo.current_balance)}
            </div>
            {(saldo.current_balance || 0) < 10000 && (
              <div className="saldo-warning">⚠ Saldo di bawah minimum</div>
            )}
          </div>

          <div className="saldo-stats">
            <div className="stat-item">
              <span className="stat-label">Total Earned</span>
              <span className="stat-value earned">{formatRupiah(saldo.total_earned)}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Withdrawn</span>
              <span className="stat-value withdrawn">{formatRupiah(saldo.total_withdrawn)}</span>
            </div>
          </div>

          <div className="saldo-card-actions">
            <button className="btn btn-add" onClick={() => onAddBalance(mitra, saldo)}>
              <span>＋</span> Tambah
            </button>
            <button className="btn btn-subtract" onClick={() => onSubtractBalance(mitra, saldo)}>
              <span>－</span> Kurangi
            </button>
            <button className="btn btn-outline" onClick={() => onViewTransactions(mitra)}>
              📋 Riwayat
            </button>
          </div>
        </>
      ) : (
        <div className="saldo-not-init">
          <div className="not-init-icon">💳</div>
          <p>Saldo belum diinisialisasi</p>
          <button className="btn btn-primary" onClick={() => onInitialize(mitra)}>
            Inisialisasi Saldo
          </button>
        </div>
      )}
    </div>
  );
}

// ========== TRANSACTION HISTORY MODAL ==========
function TransactionHistory({ mitra, isOpen, onClose }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchTransactions = useCallback(async (p = 1) => {
    if (!mitra) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/${mitra.id}/transactions`, {
        params: { page: p, limit: 10 }
      });
      setTransactions(res.data.transactions || []);
      setTotalPages(res.data.total_pages || 1);
      setPage(p);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [mitra]);

  useEffect(() => {
    if (isOpen && mitra) {
      fetchTransactions(1);
    }
  }, [isOpen, mitra, fetchTransactions]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Riwayat Transaksi - ${mitra?.nama_lengkap || ''}`}>
      {loading ? (
        <div className="loading-spinner">
          <div className="spinner" />
          <p>Memuat riwayat...</p>
        </div>
      ) : transactions.length === 0 ? (
        <div className="empty-state-mini">
          <p>Belum ada riwayat transaksi</p>
        </div>
      ) : (
        <>
          <div className="txn-list">
            {transactions.map((txn) => (
              <div key={txn.id} className={`txn-item ${txn.transaction_type}`}>
                <div className="txn-icon">
                  {txn.transaction_type === 'deposit' || txn.transaction_type === 'bonus'
                    ? '↗'
                    : '↙'}
                </div>
                <div className="txn-details">
                  <span className="txn-desc">{txn.description || txn.transaction_type}</span>
                  <span className="txn-date">
                    {new Date(txn.created_at).toLocaleDateString('id-ID', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                <div className={`txn-amount ${txn.transaction_type === 'withdrawal' ? 'negative' : 'positive'}`}>
                  {txn.transaction_type === 'withdrawal' ? '-' : '+'}
                  {formatRupiah(txn.amount)}
                </div>
              </div>
            ))}
          </div>
          <div className="pagination">
            <button
              className="btn btn-ghost btn-sm"
              disabled={page <= 1}
              onClick={() => fetchTransactions(page - 1)}
            >
              ← Prev
            </button>
            <span className="page-info">
              {page} / {totalPages}
            </span>
            <button
              className="btn btn-ghost btn-sm"
              disabled={page >= totalPages}
              onClick={() => fetchTransactions(page + 1)}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ========== MAIN APP ==========
function App() {
  const [mitras, setMitras] = useState([]);
  const [saldoMap, setSaldoMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBalance, setFilterBalance] = useState('all');
  const [toast, setToast] = useState(null);

  // Modal states
  const [balanceModal, setBalanceModal] = useState({ open: false, mitra: null, saldo: null, mode: 'add' });
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceDesc, setBalanceDesc] = useState('');
  const [balanceLoading, setBalanceLoading] = useState(false);

  const [txnModal, setTxnModal] = useState({ open: false, mitra: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };

  // Fetch all mitras
  const fetchMitras = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/mitra`);
      if (res.data.success) {
        setMitras(res.data.data || []);
      }
    } catch (err) {
      console.error('Error fetching mitras:', err);
      showToast('Gagal memuat data mitra', 'error');
    }
  };

  // Fetch saldo for a specific mitra
  const fetchSaldo = async (mitraId) => {
    try {
      const res = await axios.get(`${API_BASE}/api/${mitraId}/balance`);
      if (res.data.success) {
        setSaldoMap((prev) => ({ ...prev, [mitraId]: res.data.balance_summary }));
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setSaldoMap((prev) => ({ ...prev, [mitraId]: null }));
      } else {
        console.error(`Error fetching saldo for mitra ${mitraId}:`, err);
      }
    }
  };

  // Fetch all saldos
  const fetchAllSaldos = async (mitraList) => {
    const promises = mitraList.map((m) => fetchSaldo(m.id));
    await Promise.allSettled(promises);
  };

  // Initial load
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchMitras();
      setLoading(false);
    };
    init();
    // eslint-disable-next-line
  }, []);

  // After mitras load, fetch all saldos
  useEffect(() => {
    if (mitras.length > 0) {
      fetchAllSaldos(mitras);
    }
    // eslint-disable-next-line
  }, [mitras]);

  // Initialize saldo
  const handleInitialize = (mitra) => {
    setConfirmDialog({
      open: true,
      title: 'Inisialisasi Saldo',
      message: `Inisialisasi saldo untuk ${mitra.nama_lengkap}? Saldo awal akan diset ke Rp 0.`,
      onConfirm: async () => {
        setConfirmDialog({ open: false });
        try {
          await axios.post(`${API_BASE}/api/balance/initialize`, { mitra_id: mitra.id });
          await fetchSaldo(mitra.id);
          showToast(`Saldo ${mitra.nama_lengkap} berhasil diinisialisasi`);
        } catch (err) {
          const msg = err.response?.data?.message || 'Gagal inisialisasi saldo';
          showToast(msg, 'error');
        }
      },
    });
  };

  // Open balance modal
  const openBalanceModal = (mitra, saldo, mode) => {
    setBalanceModal({ open: true, mitra, saldo, mode });
    setBalanceAmount('');
    setBalanceDesc('');
  };

  // Submit balance change
  const handleBalanceSubmit = async () => {
    const { mitra, mode } = balanceModal;
    const amount = parseFloat(balanceAmount);

    if (!amount || amount <= 0) {
      showToast('Masukkan jumlah yang valid', 'error');
      return;
    }

    setBalanceLoading(true);
    try {
      await axios.post(`${API_BASE}/api/mitra/transactions`, {
        mitra_id: mitra.id,
        amount: amount,
        transaction_type: mode === 'add' ? 'deposit' : 'withdrawal',
        description: balanceDesc || (mode === 'add' ? 'Penambahan saldo oleh admin' : 'Pengurangan saldo oleh admin'),
      });

      await fetchSaldo(mitra.id);
      setBalanceModal({ open: false, mitra: null, saldo: null, mode: 'add' });
      showToast(
        mode === 'add'
          ? `Saldo ${mitra.nama_lengkap} berhasil ditambah ${formatRupiah(amount)}`
          : `Saldo ${mitra.nama_lengkap} berhasil dikurangi ${formatRupiah(amount)}`
      );
    } catch (err) {
      const msg = err.response?.data?.message || 'Gagal memperbarui saldo';
      showToast(msg, 'error');
    } finally {
      setBalanceLoading(false);
    }
  };

  // Quick amounts
  const quickAmounts = [10000, 25000, 50000, 100000, 250000, 500000];

  // Filter mitras
  const filteredMitras = mitras.filter((m) => {
    const q = searchQuery.toLowerCase();
    const matchSearch =
      !q ||
      m.nama_lengkap?.toLowerCase().includes(q) ||
      m.nomor_wa?.includes(q) ||
      String(m.id).includes(q);

    const matchStatus =
      filterStatus === 'all' || m.status === filterStatus;

    const saldo = saldoMap[m.id];
    let matchBalance = true;
    if (filterBalance === 'initialized') matchBalance = saldo !== null && saldo !== undefined;
    if (filterBalance === 'not-initialized') matchBalance = saldo === null || saldo === undefined;
    if (filterBalance === 'low') matchBalance = saldo && (saldo.current_balance || 0) < 10000;

    return matchSearch && matchStatus && matchBalance;
  });

  // Stats
  const totalMitras = mitras.length;
  const initializedCount = Object.values(saldoMap).filter((s) => s !== null && s !== undefined).length;
  const totalBalance = Object.values(saldoMap).reduce(
    (sum, s) => sum + (s?.current_balance || 0),
    0
  );
  const lowBalanceCount = Object.values(saldoMap).filter(
    (s) => s && (s.current_balance || 0) < 10000
  ).length;

  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner large" />
        <h2>Memuat Data Mitra...</h2>
        <p>Mohon tunggu sebentar</p>
      </div>
    );
  }

  return (
    <div className="app">
      {/* Toast */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Header */}
      <header className="app-header">
        <div className="header-left">
          <h1>
            <span className="logo-icon">💆</span> Tomassage
          </h1>
          <span className="header-subtitle">Mitra Saldo Management</span>
        </div>
        <div className="header-right">
          <button className="btn btn-ghost" onClick={() => { setLoading(true); fetchMitras().then(() => setLoading(false)); }}>
            🔄 Refresh
          </button>
        </div>
      </header>

      {/* Stats Bar */}
      <div className="stats-bar">
        <div className="stat-card">
          <div className="stat-card-value">{totalMitras}</div>
          <div className="stat-card-label">Total Mitra</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-value">{initializedCount}</div>
          <div className="stat-card-label">Saldo Aktif</div>
        </div>
        <div className="stat-card accent">
          <div className="stat-card-value">{formatRupiah(totalBalance)}</div>
          <div className="stat-card-label">Total Saldo Seluruh Mitra</div>
        </div>
        <div className={`stat-card ${lowBalanceCount > 0 ? 'warning' : ''}`}>
          <div className="stat-card-value">{lowBalanceCount}</div>
          <div className="stat-card-label">Saldo Rendah (&lt;10rb)</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filter-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Cari nama, nomor WA, atau ID mitra..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button className="search-clear" onClick={() => setSearchQuery('')}>×</button>
          )}
        </div>
        <div className="filter-group">
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">Semua Status</option>
            <option value="active">Aktif</option>
            <option value="nonactive">Nonaktif</option>
          </select>
          <select value={filterBalance} onChange={(e) => setFilterBalance(e.target.value)}>
            <option value="all">Semua Saldo</option>
            <option value="initialized">Sudah Inisialisasi</option>
            <option value="not-initialized">Belum Inisialisasi</option>
            <option value="low">Saldo Rendah</option>
          </select>
        </div>
        <div className="filter-result-count">
          Menampilkan {filteredMitras.length} dari {totalMitras} mitra
        </div>
      </div>

      {/* Mitra Saldo Grid */}
      <div className="saldo-grid">
        {filteredMitras.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h3>Tidak ada mitra ditemukan</h3>
            <p>Coba ubah filter atau kata kunci pencarian</p>
          </div>
        ) : (
          filteredMitras.map((mitra) => (
            <SaldoCard
              key={mitra.id}
              mitra={mitra}
              saldo={saldoMap[mitra.id] !== undefined ? saldoMap[mitra.id] : null}
              onAddBalance={(m, s) => openBalanceModal(m, s, 'add')}
              onSubtractBalance={(m, s) => openBalanceModal(m, s, 'subtract')}
              onViewTransactions={(m) => setTxnModal({ open: true, mitra: m })}
              onInitialize={handleInitialize}
            />
          ))
        )}
      </div>

      {/* Balance Modal */}
      <Modal
        isOpen={balanceModal.open}
        onClose={() => setBalanceModal({ open: false, mitra: null, saldo: null, mode: 'add' })}
        title={
          balanceModal.mode === 'add'
            ? `Tambah Saldo - ${balanceModal.mitra?.nama_lengkap || ''}`
            : `Kurangi Saldo - ${balanceModal.mitra?.nama_lengkap || ''}`
        }
      >
        <div className="balance-form">
          {balanceModal.saldo && (
            <div className="current-balance-display">
              <span>Saldo saat ini:</span>
              <strong>{formatRupiah(balanceModal.saldo.current_balance)}</strong>
            </div>
          )}

          <label>Jumlah ({balanceModal.mode === 'add' ? 'Tambah' : 'Kurangi'})</label>
          <div className="amount-input-wrapper">
            <span className="currency-prefix">Rp</span>
            <input
              type="number"
              placeholder="0"
              value={balanceAmount}
              onChange={(e) => setBalanceAmount(e.target.value)}
              min="0"
            />
          </div>

          <div className="quick-amounts">
            {quickAmounts.map((amt) => (
              <button
                key={amt}
                className={`quick-amt-btn ${balanceAmount === String(amt) ? 'active' : ''}`}
                onClick={() => setBalanceAmount(String(amt))}
              >
                {formatRupiah(amt)}
              </button>
            ))}
          </div>

          <label>Keterangan (opsional)</label>
          <input
            type="text"
            className="desc-input"
            placeholder="Contoh: Top up manual, bonus, dll..."
            value={balanceDesc}
            onChange={(e) => setBalanceDesc(e.target.value)}
          />

          {balanceAmount && parseFloat(balanceAmount) > 0 && (
            <div className="preview-section">
              <div className="preview-row">
                <span>Saldo sekarang</span>
                <span>{formatRupiah(balanceModal.saldo?.current_balance || 0)}</span>
              </div>
              <div className="preview-row">
                <span>{balanceModal.mode === 'add' ? 'Ditambah' : 'Dikurangi'}</span>
                <span className={balanceModal.mode === 'add' ? 'positive' : 'negative'}>
                  {balanceModal.mode === 'add' ? '+' : '-'}{formatRupiah(parseFloat(balanceAmount))}
                </span>
              </div>
              <div className="preview-row total">
                <span>Saldo akhir</span>
                <span>
                  {formatRupiah(
                    balanceModal.mode === 'add'
                      ? (balanceModal.saldo?.current_balance || 0) + parseFloat(balanceAmount)
                      : (balanceModal.saldo?.current_balance || 0) - parseFloat(balanceAmount)
                  )}
                </span>
              </div>
            </div>
          )}

          <button
            className={`btn btn-full ${balanceModal.mode === 'add' ? 'btn-add' : 'btn-subtract'}`}
            onClick={handleBalanceSubmit}
            disabled={balanceLoading || !balanceAmount || parseFloat(balanceAmount) <= 0}
          >
            {balanceLoading
              ? 'Memproses...'
              : balanceModal.mode === 'add'
              ? `Tambah ${balanceAmount ? formatRupiah(parseFloat(balanceAmount)) : ''}`
              : `Kurangi ${balanceAmount ? formatRupiah(parseFloat(balanceAmount)) : ''}`}
          </button>
        </div>
      </Modal>

      {/* Transaction History Modal */}
      <TransactionHistory
        mitra={txnModal.mitra}
        isOpen={txnModal.open}
        onClose={() => setTxnModal({ open: false, mitra: null })}
      />

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false })}
      />
    </div>
  );
}

export default App;
